import { useTranslation } from 'react-i18next';

import { formatDateTime } from '~/utils/date';

import type { RunProgress } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';
import { formatCount, stageCount } from '../utils/run-format';
import { nextStage } from '../utils/run-phase-summary';

import { useRunLabels } from './use-run-labels';

/** The run progress to describe, plus which stages gate for a decision ("Next" wording). */
export interface NowNextProps {
  progress: RunProgress;
  isGated: (stage: string) => boolean;
}

/** The "Now" / "Next" phrases as plain strings — shared by the `NowNext` sentence and the phase
 *  strip's tooltip, so both always say the same thing. `next` is null once nothing follows. */
export function useNowNext({ progress, isGated }: NowNextProps): {
  now: string;
  next: string | null;
} {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { stageLabel, statusLabel } = useRunLabels();
  const status = progress.runStatus;
  const stage = progress.currentStage ?? null;
  const gate = progress.pendingGate?.stage ?? null;
  const upcoming = nextStage(progress.stages, stage);
  const destination = progress.connectorCode
    ? catalogLabel(tCatalog, 'connector', progress.connectorCode)
    : t('nextStep.destinationFallback');

  let now: string;
  if (gate) now = t('phaseTimeline.now.gate', { stage: stageLabel(gate, destination) });
  else if (progress.paused)
    now = stage
      ? t('phaseTimeline.now.pausedAt', { stage: stageLabel(stage, destination) })
      : t('phaseTimeline.now.paused');
  else if (status === 'RUNNING' && stage) {
    const current = progress.stages.find((s) => s.stage === stage);
    const done = stageCount(current, 'products_succeeded');
    const expected = stageCount(current, 'products_expected');
    now =
      expected && done !== undefined
        ? t('phaseTimeline.now.runningCount', {
            stage: stageLabel(stage, destination),
            done: formatCount(done),
            expected: formatCount(expected),
          })
        : t('phaseTimeline.now.running', { stage: stageLabel(stage, destination) });
  } else if (status === 'PENDING') now = t('phaseTimeline.now.queued');
  else if ((status === 'FAILED' || status === 'TERMINATED') && stage)
    now = t('phaseTimeline.now.failedAt', { stage: stageLabel(stage, destination) });
  else if (status === 'COMPLETED')
    now = progress.completedAt
      ? t('phaseTimeline.now.finishedAt', { at: formatDateTime(progress.completedAt) })
      : t('phaseTimeline.now.finished');
  else now = t('phaseTimeline.now.status', { status: statusLabel(status) });

  // "Next" only means something while the run can still move forward.
  let next: string | null = null;
  if (gate) {
    next = t(`phaseTimeline.next.afterGate.${gate}`, {
      destination,
      defaultValue: t('phaseTimeline.next.afterGateGeneric'),
    });
  } else if (
    (status === 'RUNNING' || status === 'PENDING' || progress.paused) &&
    upcoming !== undefined
  ) {
    next = isGated(upcoming.stage)
      ? t('phaseTimeline.next.gated', { stage: stageLabel(upcoming.stage, destination) })
      : t('phaseTimeline.next.stage', { stage: stageLabel(upcoming.stage, destination) });
  }

  return { now, next };
}
